
const extendColor = (color: number[], matches) => {
  const result = [...color];
  matches.forEach((v: string) => { 
    if (v !== undefined) {
      result.push(Number(v));
    } else {
      result.push(1);
    }
  });
  return result;
};

const colorsFromCSS = (css: string) => {
  const rgb255 = /rgba?\((\d{1,3})\,\s*(\d{1,3})\,\s*(\d{1,3})\,?\s*(\d+\.\d{0,5})?\)/g;
  let colors: number[] = [];
  const colorString1 = rgb255.exec(css);
  const colorString2 = rgb255.exec(css);
  colors = extendColor(colors, colorString1.slice(1, 5));
  colors = extendColor(colors, colorString2.slice(1, 5));
  return colors;
};

export class BackgroundModel {
  constructor(
    public colors: number[], // 0-255
    public gradientType: 'radial' | 'linear',
    public colorStops: [number, number]
  ) { }

  static equals(a: BackgroundModel, b: BackgroundModel): boolean {
    return a.gradientType === b.gradientType // match gradient
      && a.colors.length === b.colors.length && a.colors.every((color, index) => color === b.colors[index]) // match colors
      && a.colorStops.length === b.colorStops.length && a.colorStops.every((colorStop, index) => colorStop === b.colorStops[index]); // match color stops      
  }

  // convert degenerate legacy background specs into canonical background model
  static deserialize(chapterBackground: any): BackgroundModel {

    if (!Object.keys(chapterBackground).length) {
      return null;
    }

    // convert colors to rgba e.g. [255,255,255,1]
    let colors: number[] = [];
    if (typeof chapterBackground.colors === 'string') {
      colors = colorsFromCSS(chapterBackground.colors);
    } else if (Array.isArray(chapterBackground.colors) && chapterBackground.colors.length === 8) {
      // ensure rgb and not 0-1
      colors = chapterBackground.colors.map((channel, idx) => ( (idx + 1 ) % 4 && channel <= 1.0) ? Math.round(channel * 255) : channel);
    }

    // default to radial if not a valid gradient type
    const gradientType = /radial|linear/.test(chapterBackground.gradientType) ? chapterBackground.gradientType : 'radial';
    
    // color stops should always be array of numbers between 0 and 100
    const chapterColorStops = chapterBackground.colorStops;
    const colorStops: [number, number] = Array.isArray(chapterColorStops) && chapterColorStops.length === 2
      ? [chapterColorStops[0], chapterColorStops[1]]
      : [0, 100];

    return new BackgroundModel(
      colors,
      gradientType,
      colorStops
    );
  }

  // css gradient style with 2 and only 2 color stops
  static toCSS(background: BackgroundModel) {
    const c = background.colors;
    const colorstop1 = `rgba(${c[0]},${c[1]}, ${c[2]}, ${c[3]}) ${background.colorStops[0]}%`;
    const colorstop2 = `rgba(${c[4]},${c[5]}, ${c[6]}, ${c[7]}) ${background.colorStops[1]}%`;
    if (background.gradientType === 'radial') {
      return `radial-gradient(circle, ${colorstop1}, ${colorstop2})`;
    } else {
      return `linear-gradient(180deg, ${colorstop1}, ${colorstop2})`;
    }
  }

  // from css gradient to Background Model
  static fromCSS(css: string): BackgroundModel {
    // gradient type
    const gradientType = /radial-gradient/.test(css) ? 'radial' : 'linear';

    // colors
    const colors = colorsFromCSS(css);
    
    // color stops
    const stopRX = /([0-9]+)\%/g;
    const colorStops: [number, number] = [0, 100];
    colorStops[0] = Number(stopRX.exec(css)[1]);
    colorStops[1] = Number(stopRX.exec(css)[1]);

    return new BackgroundModel(
      colors,
      gradientType,
      colorStops
    );
  }

  static isTransparent(background: BackgroundModel): boolean {
    // If not chapter is set, infer default background
    if (!background || !background.colors) {
      return false;
    }

    const colors = background.colors;
    const alpha1 = colors[3];
    const alpha2 = colors[7];
    // 4 vs 8 colors
    return colors.length <= 4 ? alpha1 === 0 : (alpha1 === 0 && alpha2 === 0);
  }
}
